import React from 'react';
import sanitizeHtml from 'sanitize-html';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AmountCredits from '@src/containers/NewPricingModelV3/AmountCredits';
import { getOptimalVouchersForPackage } from '@src/services/voucher';
import { checkVietNam } from '@src/utils/checkCountry';
import Text from '../Shared/Text';
import {
  checkCustomPackage,
  formatNumberWithSuffix,
  getCustomPackagePrice,
} from '../../../../../services/package';
import { VOUCHER_TYPE } from '../../../../../constants/voucher';
import { PACKAGE_LEVEL } from '../../../../../constants/package';

export const getCreditsWithBestVoucher = ({
  bestVoucher,
  credits,
  level,
  totalCycles,
}) => {
  if (bestVoucher?.type === VOUCHER_TYPE.CHARACTER)
    return bestVoucher?.value * credits;

  if (bestVoucher?.type === VOUCHER_TYPE.FIXED_CHARACTER) {
    if (level !== PACKAGE_LEVEL.CUSTOM) {
      const fixedCharacterPerCycle = Math.ceil(
        bestVoucher?.value / totalCycles,
      );

      return Number(fixedCharacterPerCycle) + Number(credits);
    }

    return Number(bestVoucher?.value) + Number(credits);
  }

  return credits;
};

const CreditsUsedFeature = ({ pkg, canUseVouchers, totalVoucher }) => {
  const bestVoucher = canUseVouchers(pkg)?.[0];
  const isVietNam = checkVietNam();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const optionsSelected = useSelector(
    (state) => state.packages.optionsSelected,
  );

  // Custom package
  const isCustomPackage = checkCustomPackage({
    type: pkg.type,
    level: pkg.level,
  });

  if (isCustomPackage) {
    const { price: priceVipPlusPackagePrice, vndPrice } = getCustomPackagePrice(
      {
        credits: optionsSelected.credits,
        voiceClonings: optionsSelected.voiceClonings,
        devices: optionsSelected.devices,
        concurrentRequestApi: optionsSelected.concurrentRequestApi,
        customResource: pkg?.customResource,
        isVietNam,
      },
    );

    const [bestVoucherCustomPkg] = getOptimalVouchersForPackage({
      pkg: {
        ...pkg,
        price: priceVipPlusPackagePrice,
        vndPrice,
        usdPrice: priceVipPlusPackagePrice,
        custom: { credits: optionsSelected.credits },
      },
      vouchers: totalVoucher,
    });

    const creditsWithBestVoucher = getCreditsWithBestVoucher({
      bestVoucher: bestVoucherCustomPkg,
      credits: optionsSelected?.credits,
      level: PACKAGE_LEVEL.CUSTOM,
      totalCycles: 1,
    });
    const creditsFormatted = formatNumberWithSuffix(
      creditsWithBestVoucher,
      language,
    );

    return (
      <Text
        key={pkg.code}
        html={
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t('creditsUsedPerYear', {
                  credits: creditsFormatted,
                }),
                {
                  allowedTags: ['span', 'strong'],
                  allowedAttributes: {
                    span: ['style'],
                  },
                },
              ),
            }}
          />
        }
      />
    );
  }

  return (
    <div className="flex justify-center">
      <AmountCredits
        pkg={pkg}
        bestVoucher={bestVoucher}
        hideDot
        newPricingOverviewEnabled
      />
    </div>
  );
};

const creditsUsedFeatures = ({ packages, canUseVouchers, totalVoucher }) =>
  packages.map((pkg) => (
    <CreditsUsedFeature
      key={pkg.code}
      pkg={pkg}
      canUseVouchers={canUseVouchers}
      totalVoucher={totalVoucher}
    />
  ));

export default creditsUsedFeatures;
